'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

export interface PreviousPage {
  path: string | null;
  title: string | null;
  collection: string | null;
  collectionTitle: string | null;
  isFromCollection: boolean;
}

interface NavigationState {
  currentPath: string;
  currentTitle: string;
  previousPath: string | null;
  previousTitle: string | null;
  lastCollection: string | null;
  lastCollectionTitle: string | null;
}

const STORAGE_KEY = 'navigation-history';

function cleanTitle(title: string): string {
  const pipeIndex = title.indexOf(' | ');
  return pipeIndex !== -1 ? title.substring(0, pipeIndex) : title;
}

function getStoredNavigation(): NavigationState {
  if (typeof window === 'undefined') {
    return {
      currentPath: '',
      currentTitle: '',
      previousPath: null,
      previousTitle: null,
      lastCollection: null,
      lastCollectionTitle: null,
    };
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored
      ? JSON.parse(stored)
      : {
          currentPath: '',
          currentTitle: '',
          previousPath: null,
          previousTitle: null,
          lastCollection: null,
          lastCollectionTitle: null,
        };
  } catch {
    return {
      currentPath: '',
      currentTitle: '',
      previousPath: null,
      previousTitle: null,
      lastCollection: null,
      lastCollectionTitle: null,
    };
  }
}

function updateNavigationHistory(newPath: string, newTitle: string) {
  if (typeof window === 'undefined') return;

  const stored = getStoredNavigation();
  const cleanedTitle = cleanTitle(newTitle);

  // Don't update if we're on the same page
  if (stored.currentPath === newPath) return;

  const newState: NavigationState = {
    currentPath: newPath,
    currentTitle: cleanedTitle,
    previousPath: stored.currentPath || null,
    previousTitle: stored.currentTitle || null,
    lastCollection: stored.lastCollection,
    lastCollectionTitle: stored.lastCollectionTitle,
  };

  // Update last collection if current page is a collection
  if (newPath.includes('/collections/')) {
    newState.lastCollection = newPath;
    newState.lastCollectionTitle = cleanedTitle;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  } catch {
    // Storage quota exceeded or disabled
  }
}

export function usePreviousPage(): PreviousPage {
  const pathname = usePathname();
  const [navigationState, setNavigationState] = useState<NavigationState>(
    () => {
      // Always return default state on first render to match SSR
      return {
        currentPath: '',
        currentTitle: '',
        previousPath: null,
        previousTitle: null,
        lastCollection: null,
        lastCollectionTitle: null,
      };
    }
  );
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Set hydrated flag and load actual navigation state
    setIsHydrated(true);
    setNavigationState(getStoredNavigation());
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const currentTitle = document.title;

    // Update navigation history
    updateNavigationHistory(pathname, currentTitle);

    // Update local state
    setNavigationState(getStoredNavigation());
  }, [pathname, isHydrated]);

  return {
    path: navigationState.previousPath,
    title: navigationState.previousTitle,
    collection: navigationState.lastCollection,
    collectionTitle: navigationState.lastCollectionTitle,
    isFromCollection:
      navigationState.previousPath?.includes('/collections/') ?? false,
  };
}

// Add this to your layout to track navigation on every page
export function NavigationTracker() {
  usePreviousPage();
  return null;
}
